'use client'

import Link from 'next/link'
import { useState, useEffect } from 'react'
import { Mail, Chrome, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { createClient } from '@/lib/supabase/client'
import { analytics } from '@/lib/analytics'
import { captureUtmParams } from '@/lib/utm'

export function SignupForm() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const siteUrl = process.env.NEXT_PUBLIC_APP_URL ?? window.location.origin

  useEffect(() => {
    captureUtmParams()
  }, [])

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const supabase = createClient()
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${siteUrl}/api/auth/callback`,
      },
    })

    if (error) {
      setError(error.message)
    } else {
      // Initiated only — signup_completed fires after email link confirmed (auth callback → get-started)
      analytics.ctaClicked({ location: 'signup', cta: 'magic_link_sent' })
      setSent(true)
    }
    setLoading(false)
  }

  const handleGoogleOAuth = async () => {
    setError(null)
    analytics.ctaClicked({ location: 'signup', cta: 'google_oauth' })
    const supabase = createClient()
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${siteUrl}/api/auth/callback?method=google`,
      },
    })
    if (error) {
      setError(error.message)
    }
  }

  return (
    <div className="min-h-screen bg-background text-charcoal flex flex-col justify-center items-center px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-12">
          <Link href="/" className="inline-flex items-center gap-1 mb-8">
            <span className="text-2xl font-bold">
              the<span className="text-primary">Normal</span>.ai
            </span>
          </Link>
          <h1 className="text-3xl font-bold mb-2">Get started with thenormal.ai</h1>
          <p className="text-warm-gray">Free forever. No credit card. No commitment.</p>
        </div>

        {/* Benefits */}
        <div className="bg-primary/5 rounded-lg border border-primary/10 p-6 mb-8">
          <ul className="space-y-2">
            {[
              { text: '5 free chat sessions per month' },
              { text: 'Access to all guides and news' },
              { text: 'No credit card required' },
              { text: 'Cancel anytime' },
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3">
                <span className="text-primary font-bold">✓</span>
                <span className="text-charcoal text-sm">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Signup Form */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
          {sent ? (
            <div className="text-center py-4">
              <div className="flex justify-center mb-3">
                <div className="bg-green-100 rounded-full p-3">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <p className="font-semibold text-charcoal mb-1">Check your email</p>
              <p className="text-sm text-warm-gray">
                We sent a magic link to <strong>{email}</strong>
              </p>
            </div>
          ) : (
            <>
              {/* Google OAuth — primary CTA */}
              <Button
                type="button"
                onClick={handleGoogleOAuth}
                className="w-full bg-primary hover:bg-primary/90 text-white py-3 flex items-center justify-center gap-2 mb-4"
              >
                <Chrome className="w-4 h-4" />
                Continue with Google
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-warm-gray">or use email</span>
                </div>
              </div>

              {/* Magic link — secondary */}
              <form onSubmit={handleMagicLink} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-charcoal mb-2">
                    Email address
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full px-4 py-3.5 rounded-lg border transition-colors focus:outline-none ${
                        error
                          ? 'border-red-300 bg-red-50 focus:border-red-400'
                          : 'border-gray-300 focus:border-primary'
                      }`}
                      required
                      disabled={loading}
                    />
                    {error && (
                      <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                    )}
                  </div>
                </div>

                {error && (
                  <div className="flex gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <p>{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="outline"
                  className="w-full border-gray-300 text-charcoal hover:bg-gray-50 py-3 flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  <Mail className="w-4 h-4" />
                  {loading ? 'Sending…' : 'Send magic link'}
                </Button>
              </form>
            </>
          )}
        </div>

        {/* Login Link */}
        <div className="text-center">
          <p className="text-warm-gray">
            Already have an account?{' '}
            <Link href="/login" className="text-primary font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>

        {/* Terms Notice */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <p className="text-xs text-warm-gray text-center leading-relaxed">
            By signing up, you agree to our{' '}
            <Link href="/terms" className="text-primary hover:underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </Link>
            . We&apos;ll also send you our weekly newsletter (you can unsubscribe anytime).
          </p>
        </div>
      </div>
    </div>
  )
}
